/**
 * seo.ts
 *
 * Centralized SEO configuration for the website.
 * This file defines metadata used across:
 * - Next.js <Head> tags
 * - next-seo (if used)
 * - Social previews (OpenGraph, Twitter cards)
 *
 * Fully compatible with:
 * - Node 18–24
 * - Next.js 13/14 (Pages or App Router)
 * - TypeScript 5+
 */

export interface TwitterSEO {
  handle: string;
  site: string;
  cardType: "summary" | "summary_large_image" | string;
}

export interface OGImage {
  url: string;
  width: number;
  height: number;
  alt: string;
}

export interface OpenGraphSEO {
  type: "website" | "article" | string;
  locale: string;
  url: string;
  site_name: string;
  title: string;
  description: string;
  images: OGImage[];
}

export interface SEOConfig {
  title: string;
  url: string;
  description: string;
  siteName: string;
  github: string;
  avatar: string;
  author: string;
  twitter: TwitterSEO;
  openGraph: OpenGraphSEO;
}

/**
 * Global site SEO configuration.
 */
export const SEO: SEOConfig = {
  title: "mattumm.al",
  url: "https://mattumm.al",
  description:
    "I'm a Data Engineer and an entrepreneur living in the Benelux region, EU. I enjoy creating things that live on the internet, whether that be websites, applications, or anything in between.",
  siteName: "mattumm.al",
  github: "https://github.com/mattummal/portfolio",
  avatar: "https://avatars.githubusercontent.com/u/22034038",
  author: "Prabhjyoth Mattummal",

  twitter: {
    handle: "@prbhjyth",
    site: "@prbhjyth",
    cardType: "summary_large_image",
  },

  openGraph: {
    type: "website",
    locale: "en_IE",
    url: "https://mattumm.al",
    site_name: "mattumm.al",
    title: "Rhae Mattummal",
    description:
      "Lets make things better with quality code - Learn Web Development / API's / Automations / Serverless / Architecture / and more.",
    images: [
      {
        url: "https://mattumm.al/images/sharing-image.png",
        alt: "DevOps Engineering / Cloud Development / Data Engineering",
        width: 1200,
        height: 630,
      },
    ],
  },
};

/**
 * SEO configuration for the Blog section.
 */
export const BLOG_SEO: Partial<SEOConfig> = {
  title: "Rhae Mattummal » Blog",
  description:
    "Learn Web Development - TypeScript / React / APIs / Automations / Serverless / Architecture / and more. - Lets make things better with quality code",

  openGraph: {
    title: "Rhae Mattummal » Blog",
    description:
      "Learn Web Development - TypeScript / React / APIs / Automations / Serverless / Architecture / and more. - Lets make things better with quality code",
    // the rest inherits from the main SEO object
  } as OpenGraphSEO,
};
